import { defineComponent, h, nextTick, defineAsyncComponent } from 'vue';
import { QBtn, QIcon } from 'quasar';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import EventBus from '../../event-bus.js';
import { Container, Draggable } from 'vue3-smooth-dnd';
import { ghostParentForIconOverlay } from '../vuecontrols/flowv2/canvas/iconOverlayDrag.js';
import AceEditor from '../3rdParty/AceEditor.jsx';
import Pg2Row from './Pg2Row.jsx';
import { getPropertyGrid2LeafElementType } from './elementTypesLeaves.js';
import { isPrimitiveArrayItemSchema } from './arrayItemUtils.js';

const PropertyGrid2 = defineAsyncComponent(() => import('../form/PropertyGrid2.jsx'));
const Form2ArraySelf = defineAsyncComponent(() => import('./array.jsx'));

const FormArrayLabel = defineComponent({
    name: 'form2-array-label',
    data: function () {
        return {
            titleexpn: null,
        };
    },
    async mounted() {
        if (this.cmodel.$typeSchema) {
            const schema = await utils.schema.get(this.cmodel.$typeSchema);
            const res = utils.schema.resolve_Of(schema);

            if (res.documentEditorTitle) {
                const handler1 = {
                    has: function (oTarget, sKey) {
                        return sKey !== '_self';
                    },
                    get: function (target, prop, receiver) {
                        return prop in target ? target[prop] : null;
                    },
                };
                this.model_proxy = new Proxy(this.cmodel, handler1);

                this.titleexpn = utils.compile(this, res.documentEditorTitle);
            }
        }
    },
    computed: {
        title: function () {
            if (this.titleexpn)
                try {
                    return utils.evaluate(this.titleexpn, this.model_proxy);
                } catch (e) {
                    utils.warn('Array documentEditorTitle failed to evaluate: ' + this.titleexpn + '; ' + e);
                    return null;
                }
            else return null;
        },
    },
    props: {
        cmodel: null,
        visible: false,
    },
    render() {
        const style = {
            fontFamily: 'Arial',
            fontSize: 'small',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: '350px',
            marginTop: '2px',
            marginBottom: '1px',
        };
        return (
            <div title={this.title} style={style}>
                {this.title}
            </div>
        );
    },
});

function toggleVisible(control, key) {
    const default_value = 'true';
    const value = control.visible_keys[key] || localStorage.getItem(key);

    if (!value) {
        const new_value = default_value === 'true' ? 'false' : 'true';
        localStorage.setItem(key, new_value);
        control.visible_keys[key] = new_value;
        utils.log('Saved ' + key + ' as ' + new_value);
    } else {
        const new_value = value === 'true' ? 'false' : 'true';
        if (new_value === default_value) {
            localStorage.removeItem(key);
            delete control.visible_keys[key];
            utils.log('Deleted ' + key);
        } else {
            localStorage.setItem(key, new_value);
            control.visible_keys[key] = new_value;
            utils.log('Saved ' + key + ' as ' + new_value);
        }
    }
}

function getVisibility(control, key) {
    const default_value = 'true';
    let value = control.visible_keys[key];
    if (!value) {
        value = localStorage.getItem(key);
        if (value) control.visible_keys[key] = value;
    }

    if (!value) return default_value === 'true';
    else return value === 'true';
}

export default defineComponent({
    name: 'form2-array',
    components: {
        QBtn,
        QIcon,
        AceEditor,
        PropertyGrid2,
        FormArrayLabel,
        Pg2Row,
    },
    data: function () {
        return {
            selected: [],
            visible_keys: {},

            uniquekey: null,
            asjson: false,
            rawJsonEditorHeight: '100px',
            itemRowKeys: [],
        };
    },
    created() {
        this.uniquekey = utils.generateUUID();
    },
    /** No provide() for pg2SplitPointerDown: array property rows inherit the outer PropertyGrid2 splitter.
     *  Nested PropertyGrid2 inside an item still provide() their own handler, so their Pg2Rows win by inject proximity. */
    mounted() {
        nextTick(() => this.ensureRawJsonAceResizeObserver());
    },
    updated() {
        nextTick(() => this.ensureRawJsonAceResizeObserver());
    },
    beforeUnmount() {
        this.teardownRawJsonAceResizeObserver();
    },
    computed: {
        ...utils.forms.computed,
        anySelected: function () {
            if (!this.selected || this.selected.length == 0) return false;
            for (let i = 0; i < this.selected.length; i++) if (this.selected[i]) return true;
            return false;
        },
    },
    methods: {
        ...methods,
        ensureRawJsonAceResizeObserver() {
            if (typeof ResizeObserver === 'undefined') return;
            const host = this.$refs.arrayRawJsonAceHost;
            if (!host) {
                this.teardownRawJsonAceResizeObserver();
                return;
            }
            if (this._arrayRawJsonResizeObserved === host) return;
            this.teardownRawJsonAceResizeObserver();
            this._arrayRawJsonResizeObserved = host;
            this._arrayRawJsonResizeObserver = new ResizeObserver(() => {
                const px = Math.round(host.offsetHeight || 0);
                if (!px) return;
                const next = `${px}px`;
                if (this.rawJsonEditorHeight !== next) this.rawJsonEditorHeight = next;
            });
            this._arrayRawJsonResizeObserver.observe(host);
        },
        teardownRawJsonAceResizeObserver() {
            if (this._arrayRawJsonResizeObserver) {
                this._arrayRawJsonResizeObserver.disconnect();
                this._arrayRawJsonResizeObserver = null;
            }
            this._arrayRawJsonResizeObserved = null;
        },
        ensureItemRowKeys(length) {
            while (this.itemRowKeys.length < length) {
                this.itemRowKeys.push(utils.generateUUID());
            }
            if (this.itemRowKeys.length > length) {
                this.itemRowKeys.length = length;
            }
        },
        resetItemRowKeys(length) {
            this.itemRowKeys = [];
            this.ensureItemRowKeys(length);
        },
        getItemRowKey(i, item) {
            if (item && typeof item === 'object' && item.$objectId != null) {
                return String(item.$objectId);
            }
            return this.itemRowKeys[i] || 'row_' + i;
        },
        async addAnother() {
            if (!this.cmodel[this.schemakey]) this.cmodel[this.schemakey] = [];

            const schema = utils.schema.resolve_Of(this.schema.items);
            const newmodel = utils.schema.createDefaultModel(schema, this.root);
            this.cmodel[this.schemakey].push(newmodel);
            this.itemRowKeys.push(utils.generateUUID());
        },
        removeItem(index) {
            this.cmodel[this.schemakey].splice(index, 1);
            this.itemRowKeys.splice(index, 1);
        },
        moveItemUp(index) {
            if (index > 0) {
                const item = this.cmodel[this.schemakey][index];
                this.cmodel[this.schemakey].splice(index, 1);
                this.cmodel[this.schemakey].splice(index - 1, 0, item);
                const key = this.itemRowKeys[index];
                this.itemRowKeys.splice(index, 1);
                this.itemRowKeys.splice(index - 1, 0, key);
            }
        },
        moveItemDown(index) {
            if (index < this.cmodel[this.schemakey].length - 1) {
                const item = this.cmodel[this.schemakey][index];
                this.cmodel[this.schemakey].splice(index, 1);
                this.cmodel[this.schemakey].splice(index + 1, 0, item);
                const key = this.itemRowKeys[index];
                this.itemRowKeys.splice(index, 1);
                this.itemRowKeys.splice(index + 1, 0, key);
            }
        },

        setSelection(index, checked) {
            while (index >= this.selected.length) this.selected.push(false);

            this.selected[index] = checked;
        },
        async copy() {
            const controls = this.cmodel[this.schemakey];
            const selected = [];
            for (let i = 0; i < this.selected.length; i++) if (this.selected[i]) selected.push(controls[i]);

            await utils.helpers.writeCareClipboard(selected);
        },
        async cut() {
            const controls = this.cmodel[this.schemakey];
            const selected = [];
            for (let i = 0; i < this.selected.length; i++) if (this.selected[i]) selected.push(controls[i]);

            await utils.helpers.writeCareClipboard(selected);

            for (let i = this.selected.length - 1; i >= 0; i--) {
                if (this.selected[i]) {
                    controls.splice(i, 1);
                    this.itemRowKeys.splice(i, 1);
                }
            }

            this.selected = [];
        },
        async paste() {
            const data = await utils.helpers.readCareClipboardArray();
            if (!data) {
                alert('Failed to parse data from clipboard buffer');
                return;
            }
            if (!this.cmodel[this.schemakey]) this.cmodel[this.schemakey] = [];

            const controls = this.cmodel[this.schemakey];
            for (let i = 0; i < data.length; i++) {
                if ('$objectId' in data[i]) data[i].$objectId = utils.generateUUID();
                controls.push(data[i]);
                this.itemRowKeys.push(utils.generateUUID());
            }
        },
        selectAll() {
            const controls = this.cmodel[this.schemakey];

            this.selected = new Array(controls.length);

            for (let i = 0; i < this.selected.length; i++) this.selected[i] = true;
        },
        selectNone() {
            this.selected = [];
        },
        extractor(control, prop, index) {
            const accept = async (value) => {
                const newmodel = control.cmodel[index];
                let newcontrol;
                let replaced;
                let type;
                switch (this.schema.items.Id) {
                    case '/Apps/Schema/AnyOf/schema/public/Platform.Schema.BaseTypes.v1/DynamicControl':
                        type = 'UserControl';
                        newcontrol = {
                            $typeSchema: '/schema/public/Platform.Schema.Documents.v1/Document_UserControl',
                            Name: value,
                            Data: newmodel,
                            Public: false,
                            ControlDataSchema: {
                                type: 'object',
                                additionalProperties: true,
                                properties: {},
                            },
                            $DeployID: 'dev',
                        };
                        replaced = {
                            $typeSchema: '/schema/public/Platform.Schema.DynamicControls.v1/DynamicControl_Reference',
                            PrimitiveControl: false,
                            CacheControl: true,
                            ControlData: {},
                            ControlURL: newcontrol.Name,
                            $objectId: utils.generateUUID(),
                        };
                        break;

                    case '/Apps/Schema/AnyOf/schema/public/Platform.Schema.BaseTypes.v1/UIAction':
                        type = 'Action';
                        newcontrol = {
                            $typeSchema: '/schema/public/Platform.Schema.Documents.v1/Document_UserAction',
                            Name: value,
                            Data: newmodel,
                            Public: false,
                            ActionDataSchema: {
                                type: 'object',
                                additionalProperties: true,
                                properties: {},
                            },
                            $DeployID: 'dev',
                        };
                        replaced = {
                            $typeSchema: '/schema/public/Platform.Schema.UIActions.v1/UIAction_Reference',
                            PrimitiveAction: true,
                            ActionType: 'ReferenceUserAction',
                            ActionData: {
                                UserAction: {
                                    ActionData: {},
                                    ActionURL: newcontrol.Name,
                                    CacheAction: true,
                                },
                            },
                            RunAsync: false,
                            $objectId: utils.generateUUID(),
                        };
                        break;

                    default:
                        return;
                }

                if (this.root.saveExtractedEntity) {
                    const success = await this.root.saveExtractedEntity(type, newcontrol);

                    if (success) control.cmodel[index] = replaced;
                }
            };

            let title;
            let label;
            switch (this.schema.items.Id) {
                case '/Apps/Schema/AnyOf/schema/public/Platform.Schema.BaseTypes.v1/DynamicControl':
                    title = 'Extract User Control';
                    label = 'New User Control Name';
                    break;

                case '/Apps/Schema/AnyOf/schema/public/Platform.Schema.BaseTypes.v1/UIAction':
                    title = 'Extract User Action';
                    label = 'New User Action Name';
                    break;

                default:
                    return;
            }

            EventBus.$emit(
                'SHOW_DIALOG',
                <input-dialog root={this.root} title={title} label={label} accept={accept}></input-dialog>,
            );
        },
        reOrderItems(result) {
            if (result.removedIndex == null || result.addedIndex == null) return;
            const cmodel = this.cmodel[this.schemakey] || [];
            const item = cmodel[result.removedIndex];
            cmodel.splice(result.removedIndex, 1);
            cmodel.splice(result.addedIndex, 0, item);
            const key = this.itemRowKeys[result.removedIndex];
            this.itemRowKeys.splice(result.removedIndex, 1);
            this.itemRowKeys.splice(result.addedIndex, 0, key);
        },

        sync(text) {
            try {
                const parsed = JSON.parse(text);
                this.cmodel[this.schemakey] = parsed;
                this.resetItemRowKeys(Array.isArray(parsed) ? parsed.length : 0);
            } catch (e) {
                utils.warn('array in propertygrid could not parse JSON', e);
            }
        },
    },
    props: {
        name: '',
        schemakey: '',
        root: null,
        schema: null,
        cmodel: null,
        extra: null,
        readonly: false,
        path: null,
        child: 0,
        navigateTo: null,
        canNavigate: false,
        propertygrid: false,
    },
    render() {
        if (!this.Condition || !this.schema || !this.schema.items) return null;

        let desc;
        if (this.schema.description)
            desc = (
                <i
                    class="mdi mdi-information"
                    title={this.schema.description}
                    style={{ color: 'var(--v-checkboxGrey-base)', marginLeft: '3px' }}
                ></i>
            );

        let err;
        if (this.schema.formatData?.error)
            err = utils.tt(this.schema.formatData.error, <QIcon name="mdi-alert" size="xs" color="negative" />);

        const schema = utils.schema.resolve_Of(this.schema.items);
        const name = <span style={{ marginLeft: this.child * 15 + 'px' }}>{this.name}</span>;

        const heads = [];
        const foots = [];
        let toolbarRow = null;
        const draggableChildren = [];
        const cmodel = this.cmodel[this.schemakey] || [];

        const visible_key = [...(this.path || []), this.schemakey].join('.');
        const vis = getVisibility(this, visible_key);
        const icon = vis ? <i class="mdi mdi-menu-down"></i> : <i class="mdi mdi-chevron-right-box-outline"></i>;
        const openclose = (
            <span style={{ cursor: 'pointer' }} onClick={() => toggleVisible(this, visible_key)}>
                {icon}
            </span>
        );

        const jsonico = this.asjson ? (
            <i class="mdi mdi-eye-off" title="JSON view"></i>
        ) : (
            <i class="mdi mdi-eye" title="JSON view"></i>
        );
        const jsontgl = (
            <span style={{ cursor: 'pointer', color: 'gray' }} onClick={() => (this.asjson = !this.asjson)}>
                {jsonico}
            </span>
        );

        const tdstyle = {
            ...(this.propertygrid ? { minWidth: 0 } : { backgroundColor: '#f8f8f8' }),
        };

        const labelSlot = () => (
            <span class="c-PropertyGrid2__labelInner">
                {name} {jsontgl} {openclose} {desc}
                {err}
            </span>
        );

        if (vis) {
            if (this.asjson || typeof cmodel === 'string') {
                const rawjson = JSON.stringify(cmodel, null, 4);
                return (
                    <Pg2Row>
                        {{
                            label: labelSlot,
                            default: () => (
                                <div
                                    ref="arrayRawJsonAceHost"
                                    class="c-PropertyGrid2__editorHost"
                                    style={{
                                        resize: 'vertical',
                                        width: '100%',
                                        height: this.rawJsonEditorHeight,
                                        padding: '5px',
                                        overflow: 'auto',
                                        position: 'relative',
                                        minWidth: 0,
                                    }}
                                >
                                    <AceEditor
                                        editorId={'Editor_' + this.uniquekey}
                                        lang="json"
                                        onChangeContent={(text) => this.sync(text)}
                                        content={rawjson}
                                    ></AceEditor>
                                </div>
                            ),
                        }}
                    </Pg2Row>
                );
            }

            const btn_styles = {
                'font-size': 'x-small',
            };
            const enabledBtnProps = {
                unelevated: true,
                size: 'sm',
                color: 'blue-grey',
                textColor: 'white',
                style: btn_styles,
                dense: true,
                class: ['q-px-sm'],
            };
            const disabledBtnProps = {
                unelevated: true,
                size: 'sm',
                color: 'grey-5',
                textColor: 'grey-8',
                style: btn_styles,
                dense: true,
                class: ['q-px-sm'],
            };

            if (this.canNavigate) {
                const copyCutDisabled = !this.anySelected;
                toolbarRow = (
                    <span class={{ 'property-grid': this.propertygrid }} style="display:flex; gap: 5px; marginBottom: 6px">
                        <QBtn {...(copyCutDisabled ? disabledBtnProps : enabledBtnProps)} disable={copyCutDisabled} onClick={this.copy}>
                            Copy
                        </QBtn>
                        <QBtn {...(copyCutDisabled ? disabledBtnProps : enabledBtnProps)} disable={copyCutDisabled} onClick={this.cut}>
                            Cut
                        </QBtn>
                        <QBtn {...enabledBtnProps} onClick={this.paste}>
                            Paste
                        </QBtn>

                        <QBtn {...enabledBtnProps} onClick={this.selectAll}>
                            Select All
                        </QBtn>
                        <QBtn {...enabledBtnProps} onClick={this.selectNone}>
                            Select None
                        </QBtn>
                    </span>
                );
            }

            let Tag = getPropertyGrid2LeafElementType(schema);
            if (!Tag && schema.type == 'object') Tag = PropertyGrid2;
            if (!Tag && schema.type == 'array') Tag = Form2ArraySelf;

            const primitiveInline = this.propertygrid && isPrimitiveArrayItemSchema(schema);

            if (Tag) {
                this.ensureItemRowKeys(cmodel.length);
                for (let i = 0; i < cmodel.length; i++) {
                    const item = cmodel[i];

                    let select_checkbox;
                    let item_label;
                    let style = {
                        marginBottom: '2px',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                    };

                    if (this.canNavigate) {
                        select_checkbox = (
                            <input
                                type="checkbox"
                                checked={this.selected.length > i && this.selected[i]}
                                onChange={(e) => this.setSelection(i, e.target.checked)}
                            />
                        );
                        item_label = (
                            <FormArrayLabel key={i + '_' + (item ? item.$typeSchema : 'null')} cmodel={item} visible={true}>
                            </FormArrayLabel>
                        );
                    }

                    if (item && typeof item === 'object' && !item.$objectId) item.$objectId = utils.generateUUID();

                    const tagEl = (
                        <Tag
                            name={'[' + i + ']'}
                            schemakey={i}
                            schema={schema}
                            root={this.root}
                            cmodel={cmodel}
                            form_model={this.$attrs.form_model}
                            child={0}
                            readonly={schema.readonly || false}
                            navigateTo={(control, prop, value) => this.navigateTo(control, prop, value || item)}
                            canNavigate={this.canNavigate}
                            extractor={(control, prop) => this.extractor(control, prop, i)}
                            propertygrid={this.propertygrid}
                            arrayItemInline={primitiveInline}
                        >
                        </Tag>
                    );

                    const buttons = (
                        <>
                            <QBtn
                                unelevated
                                flat
                                size="sm"
                                class="q-px-none"
                                icon="mdi-arrow-up"
                                textColor={i === 0 ? 'grey-5' : 'grey-7'}
                                title="Move Up"
                                style={btn_styles}
                                disable={i === 0}
                                onClick={() => this.moveItemUp(i)}
                            />
                            <QBtn
                                unelevated
                                flat
                                size="sm"
                                class="q-px-none"
                                icon="mdi-arrow-down"
                                textColor={i == cmodel.length - 1 ? 'grey-5' : 'grey-7'}
                                title="Move Down"
                                style={btn_styles}
                                disable={i == cmodel.length - 1}
                                onClick={() => this.moveItemDown(i)}
                            />
                            <QBtn
                                unelevated
                                flat
                                size="sm"
                                class="q-px-none"
                                icon="mdi-close"
                                textColor="grey-7"
                                title="Delete"
                                style={btn_styles}
                                onClick={() => this.removeItem(i)}
                            />
                            <span
                                v-show={cmodel.length > 1}
                                class="column-drag-handle"
                                style={
                                    primitiveInline
                                        ? { cursor: 'grab', flexShrink: 0, padding: '0 6px', alignSelf: 'center' }
                                        : { float: 'right', padding: '0 10px', marginTop: '5px', cursor: 'grab' }
                                }
                            >
                                &#x2630;
                            </span>
                        </>
                    );

                    draggableChildren.push(
                        <Draggable key={this.getItemRowKey(i, item)} index={i} style={style}>
                            {primitiveInline ? (
                                <div>
                                    <div class={{ 'c-PropertyGrid2__arrayItemRow': true, 'property-grid': this.propertygrid }}>
                                        {select_checkbox}
                                        <div class="c-PropertyGrid2__arrayItemRow__grow">{tagEl}</div>
                                        {buttons}
                                    </div>
                                    {item_label ? <div class="c-PropertyGrid2__arrayItemRow__subtitle">{item_label}</div> : null}
                                </div>
                            ) : (
                                <>
                                    <span class={{ 'draggable-item': true, 'property-grid': this.propertygrid }}>
                                        {select_checkbox}
                                        {tagEl}
                                        {buttons}
                                    </span>
                                    <br />
                                    {item_label}
                                </>
                            )}
                        </Draggable>,
                    );
                }
            }

            foots.push(
                <span class={{ 'property-grid': this.propertygrid }}>
                    <QBtn {...enabledBtnProps} class={['q-px-sm', 'c-SentenceCaseButton']} no-caps onClick={() => this.addAnother()} icon="mdi-plus">
                        <span>Add Another</span>
                    </QBtn>
                </span>,
            );
        } else heads.push(<span>{cmodel.length} Item(s)</span>);

        return (
            <Pg2Row>
                {{
                    label: labelSlot,
                    default: () => (
                        <div style={tdstyle}>
                            {heads}
                            {toolbarRow}
                            <Container onDrop={(res) => this.reOrderItems(res)} drag-handle-selector=".column-drag-handle" getGhostParent={() => ghostParentForIconOverlay(this.$el)}>
                                {draggableChildren}
                            </Container>
                            {foots}
                        </div>
                    ),
                }}
            </Pg2Row>
        );
    },
});
